import React, { useState } from 'react';
import api from '../api';
import { ImportResult, ImportError, IMPORT_FORMATS } from '../types/order';

interface OrderImportProps {
  onImportComplete: () => void;
}

// Fields required/optional for creating an order in Nexus
const NEXUS_ORDER_FIELDS = [
  { key: 'order_id', label: 'Order ID (External)', required: false, description: 'External order number' },
  { key: 'customer_name', label: 'Customer Name', required: true, description: 'Customer name' },
  { key: 'email', label: 'Email', required: false, description: 'Customer email' },
  { key: 'phone', label: 'Phone', required: false, description: 'Customer phone' },
  { key: 'address1', label: 'Address Line 1', required: true, description: 'Street address line 1' },
  { key: 'address2', label: 'Address Line 2', required: false, description: 'Street address line 2' },
  { key: 'city', label: 'City', required: true, description: 'City' },
  { key: 'state', label: 'State', required: false, description: 'State' },
  { key: 'pincode', label: 'Pincode', required: true, description: 'Postal code / Pincode' },
  { key: 'country', label: 'Country', required: false, description: 'Country (defaults to India)' },
  { key: 'product_name', label: 'Product Name / SKU', required: true, description: 'Product to match in catalog' },
  { key: 'quantity', label: 'Quantity', required: false, description: 'Defaults to 1' },
  { key: 'unit_price', label: 'Unit Price', required: false, description: 'Uses product price if blank' },
  { key: 'order_status', label: 'Order Status', required: false, description: 'received/processing/shipped/delivered/cancelled' },
  { key: 'order_date', label: 'Order Date', required: false, description: 'Date order was placed' },
  { key: 'channel', label: 'Channel', required: false, description: 'Sales channel name' },
  { key: 'notes', label: 'Notes', required: false, description: 'Order notes' },
];

const OrderImport: React.FC<OrderImportProps> = ({ onImportComplete }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [format, setFormat] = useState<string>(IMPORT_FORMATS.VINERETAIL);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Custom mapping state
  const [step, setStep] = useState<'select' | 'mapping'>('select');
  const [detectedColumns, setDetectedColumns] = useState<string[]>([]);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [previewRows, setPreviewRows] = useState<any[]>([]);

  // Channel state
  const [channels, setChannels] = useState<any[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<any>(null);

  // Duplicate check state
  const [checkDuplicates, setCheckDuplicates] = useState(false);

  // Fetch channels on mount
  React.useEffect(() => {
    api.get('/channels/').then(res => {
      setChannels(res.data || []);
    }).catch(() => {});
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
      setImportResult(null);
      setError(null);
      setStep('select');
      setDetectedColumns([]);
      setFieldMapping({});
    }
  };

  const handleFormatChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setFormat(val);
    setImportResult(null);
    setError(null);
    setStep('select');
    setDetectedColumns([]);
    setFieldMapping({});
    // Check if it's a channel format (starts with "channel:")
    if (val.startsWith('channel:')) {
      const channelId = parseInt(val.split(':')[1]);
      const ch = channels.find(c => c.id === channelId);
      setSelectedChannel(ch || null);
    } else {
      setSelectedChannel(null);
    }
  };

  const isChannelFormat = format.startsWith('channel:');
  const isCustomLike = format === IMPORT_FORMATS.CUSTOM || isChannelFormat;
  const channelHasTemplate = selectedChannel?.order_template_config?.columns && Object.keys(selectedChannel.order_template_config.columns).length > 0;

  const handleDetectColumns = async () => {
    if (!selectedFile) return;
    setError(null);
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      const response = await api.post('/orders/import/detect-columns', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDetectedColumns(response.data.columns || []);
      setPreviewRows(response.data.preview || []);

      // If channel selected and has saved template, apply its mapping
      if (selectedChannel && selectedChannel.order_template_config) {
        const templateCols = selectedChannel.order_template_config.columns || {};
        // templateCols maps nexus_field_key -> file_column_name
        setFieldMapping(templateCols);
      } else {
        setFieldMapping(response.data.auto_mapping || {});
      }
      setStep('mapping');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to detect columns');
    }
  };

  const handleChannelImport = async () => {
    if (!selectedFile || !selectedChannel) return;
    setImporting(true);
    setError(null);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('mapping', JSON.stringify(selectedChannel.order_template_config.columns));
      formData.append('channel', selectedChannel.code || selectedChannel.name);
      formData.append('check_duplicates', checkDuplicates ? 'true' : 'false');
      const response = await api.post('/orders/import?format=custom', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(response.data);
      if (response.data.success_count > 0) onImportComplete();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleCustomImport = async () => {
    if (!selectedFile) return;
    const missingRequired = NEXUS_ORDER_FIELDS.filter(f => f.required && !fieldMapping[f.key]).map(f => f.label);
    if (missingRequired.length > 0) {
      setError(`Map required fields: ${missingRequired.join(', ')}`);
      return;
    }
    setImporting(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('mapping', JSON.stringify(fieldMapping));
      formData.append('check_duplicates', checkDuplicates ? 'true' : 'false');
      if (selectedChannel) {
        formData.append('channel', selectedChannel.code || selectedChannel.name);
      }
      const response = await api.post('/orders/import?format=custom', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(response.data);
      if (response.data.success_count > 0) onImportComplete();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setError('Please select a file to import');
      return;
    }

    setImporting(true);
    setError(null);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post(`/orders/import?format=${format}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setImportResult(response.data);
      
      // If import was successful (at least some orders imported), notify parent
      if (response.data.success_count > 0) {
        onImportComplete();
      }
    } catch (err: any) {
      console.error('Import error:', err);
      
      let errorMessage = 'Failed to import orders';
      
      if (err.response?.status === 400) {
        errorMessage = err.response?.data?.detail || 'Invalid file format or data';
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setImportResult(null);
    setError(null);
    setFormat(IMPORT_FORMATS.VINERETAIL);
    setStep('select');
    setDetectedColumns([]);
    setFieldMapping({});
    setPreviewRows([]);
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const getResultSummaryStyle = () => {
    if (!importResult) return {};
    
    if (importResult.error_count === 0 && importResult.duplicate_count === 0 && importResult.success_count > 0) {
      return { backgroundColor: '#d1fae5', borderColor: '#10b981' };
    } else if (importResult.success_count > 0) {
      return { backgroundColor: '#fef3c7', borderColor: '#f59e0b' };
    } else {
      return { backgroundColor: '#fee2e2', borderColor: '#ef4444' };
    }
  };

  return (
    <div style={{ maxWidth: '900px', width: '100%' }}>
      <h3>Import Orders</h3>
      
      {/* Import Form */}
      <div style={{ 
        padding: '1.5rem', 
        backgroundColor: '#f9fafb', 
        borderRadius: '8px',
        marginBottom: '1.5rem',
        border: '1px solid #e5e7eb'
      }}>
        <div className="form-group">
          <label className="form-label">Import Format: *</label>
          <select
            value={format}
            onChange={handleFormatChange}
            disabled={importing}
            className="form-select"
            style={{ maxWidth: '300px' }}
          >
            <option value={IMPORT_FORMATS.VINERETAIL}>vineretail ERP</option>
            <option value={IMPORT_FORMATS.MAGENTO}>Magento</option>
            <option value={IMPORT_FORMATS.CUSTOM}>Custom (Map Fields)</option>
            {channels.length > 0 && (
              <optgroup label="Channels">
                {channels.map((ch: any) => (
                  <option key={ch.id} value={`channel:${ch.id}`}>
                    {ch.name} ({ch.code})
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <small style={{ display: 'block', marginTop: '0.5rem', color: '#64748b', fontSize: '0.875rem' }}>
            {isCustomLike ? 'Upload file → map columns → import' : 'Select the format of your import file'}
          </small>
        </div>

        <div className="form-group">
          <label className="form-label">Select File: *</label>
          <input
            id="file-input"
            type="file"
            onChange={handleFileChange}
            disabled={importing}
            accept=".csv,.xlsx,.xls"
            className="form-input"
            style={{ maxWidth: '500px' }}
          />
          {selectedFile && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#475569' }}>
              Selected: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(2)} KB)
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#334155', cursor: 'pointer', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={checkDuplicates}
              onChange={(e) => setCheckDuplicates(e.target.checked)}
              disabled={importing}
              style={{ width: '16px', height: '16px', accentColor: '#0d9488', cursor: 'pointer' }}
            />
            Check for duplicates (email/phone)
          </label>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          {isCustomLike && !channelHasTemplate ? (
            <button className="btn btn-primary" onClick={handleDetectColumns} disabled={!selectedFile || importing || step === 'mapping'}>
              {step === 'mapping' ? '✓ Columns Detected' : 'Detect Columns →'}
            </button>
          ) : isCustomLike && channelHasTemplate ? (
            <>
              <button className="btn btn-primary" onClick={handleChannelImport} disabled={!selectedFile || importing}>
                {importing ? 'Importing...' : 'Import Orders'}
              </button>
              <button className="btn btn-secondary" onClick={handleDetectColumns} disabled={!selectedFile || importing || step === 'mapping'}>
                Adjust Mapping
              </button>
            </>
          ) : (
            <button className="btn btn-primary" onClick={handleImport} disabled={!selectedFile || importing}>
              {importing ? 'Importing...' : 'Import Orders'}
            </button>
          )}
          <button className="btn btn-secondary" onClick={handleReset} disabled={importing}>Reset</button>
        </div>
      </div>

      {/* Custom Field Mapping Step */}
      {isCustomLike && step === 'mapping' && !importResult && (
        <div style={{ padding: '1.5rem', backgroundColor: '#f0fdfa', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid #99f6e4' }}>
          <h4 style={{ marginTop: 0, marginBottom: '0.75rem', color: '#0d9488' }}>Map Columns to Order Fields</h4>
          <p style={{ fontSize: '13px', color: '#475569', marginBottom: '1rem' }}>
            Detected <strong>{detectedColumns.length}</strong> columns. Map them to Nexus fields below. <span style={{ color: '#ef4444' }}>*</span> = required.
          </p>

          {previewRows.length > 0 && (
            <div style={{ marginBottom: '1rem', overflowX: 'auto', maxWidth: '100%' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '4px', color: '#64748b' }}>Preview:</div>
              <table style={{ fontSize: '11px', borderCollapse: 'collapse' }}>
                <thead><tr>{detectedColumns.map(c => <th key={c} style={{ padding: '3px 6px', background: '#e2e8f0', border: '1px solid #cbd5e1', whiteSpace: 'nowrap' }}>{c}</th>)}</tr></thead>
                <tbody>{previewRows.slice(0, 2).map((row, i) => <tr key={i}>{detectedColumns.map(c => <td key={c} style={{ padding: '3px 6px', border: '1px solid #e2e8f0', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row[c] ?? ''}</td>)}</tr>)}</tbody>
              </table>
            </div>
          )}

          <div className="form-grid-2" style={{ gap: '6px', marginBottom: 0 }}>
            {NEXUS_ORDER_FIELDS.map(field => (
              <div key={field.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', background: 'white', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '12px', fontWeight: 600 }}>{field.label}{field.required && <span style={{ color: '#ef4444' }}> *</span>}</span>
                </div>
                <select value={fieldMapping[field.key] || ''} onChange={(e) => setFieldMapping({ ...fieldMapping, [field.key]: e.target.value })} style={{ width: '160px', padding: '3px 6px', border: '1px solid #dde2e8', borderRadius: '4px', fontSize: '12px' }}>
                  <option value="">— skip —</option>
                  {detectedColumns.map(col => <option key={col} value={col}>{col}</option>)}
                </select>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '1rem', display: 'flex', gap: '8px' }}>
            <button className="btn btn-primary" onClick={handleCustomImport} disabled={importing}>{importing ? 'Importing...' : 'Import with Mapping'}</button>
            <button className="btn btn-secondary" onClick={() => setStep('select')}>Back</button>
          </div>
        </div>
      )}

      {/* Progress Indicator */}
      {importing && (
        <div style={{
          padding: '1.5rem',
          backgroundColor: '#dbeafe',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          border: '1px solid #60a5fa',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1rem', color: '#1e40af', marginBottom: '0.5rem' }}>
            <strong>Importing orders...</strong>
          </div>
          <div style={{ fontSize: '0.875rem', color: '#475569' }}>
            Please wait while we process your file
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && !importing && (
        <div style={{
          padding: '1.5rem',
          backgroundColor: '#fee2e2',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          border: '1px solid #ef4444'
        }}>
          <div style={{ fontSize: '1rem', color: '#991b1b', fontWeight: '600', marginBottom: '0.5rem' }}>
            Import Failed
          </div>
          <div style={{ fontSize: '0.875rem', color: '#7f1d1d' }}>
            {error}
          </div>
        </div>
      )}

      {/* Results Summary */}
      {importResult && !importing && (
        <div>
          <div style={{
            padding: '1.5rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '2px solid',
            ...getResultSummaryStyle()
          }}>
            <h4 style={{ marginTop: 0, marginBottom: '1rem' }}>Import Summary</h4>
            
            <div className="form-grid-3" style={{ gap: '1rem' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#16a34a' }}>
                  {importResult.success_count}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#475569' }}>
                  Successfully Imported
                </div>
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>
                  {importResult.duplicate_count}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#475569' }}>
                  Duplicates Skipped
                </div>
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ef4444' }}>
                  {importResult.error_count}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#475569' }}>
                  Errors
                </div>
              </div>
            </div>

            {importResult.imported_order_ids.length > 0 && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'rgba(255, 255, 255, 0.7)', borderRadius: '4px' }}>
                <strong>Imported Order IDs:</strong> {importResult.imported_order_ids.join(', ')}
              </div>
            )}
          </div>

          {/* Error Details Table */}
          {importResult.errors.length > 0 && (
            <div>
              {/* Duplicate Errors - highlighted separately */}
              {importResult.errors.filter((err: ImportError) => err.type === 'duplicate').length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ marginBottom: '0.75rem', color: '#92400e' }}>
                    Duplicate Records (Email/Phone already exists in channel)
                  </h4>
                  <div className="table-container" style={{ border: '1px solid #f59e0b', borderRadius: '8px', overflow: 'hidden' }}>
                    <table className="table" style={{ marginBottom: 0 }}>
                      <thead>
                        <tr style={{ background: '#fffbeb' }}>
                          <th style={{ width: '80px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', color: '#92400e', letterSpacing: '0.5px' }}>Row</th>
                          <th style={{ width: '120px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', color: '#92400e', letterSpacing: '0.5px' }}>Match Field</th>
                          <th style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', color: '#92400e', letterSpacing: '0.5px' }}>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.errors
                          .filter((err: ImportError) => err.type === 'duplicate')
                          .map((err: ImportError, index: number) => (
                          <tr key={`dup-${index}`} style={{ backgroundColor: '#fffbeb' }}>
                            <td>
                              <span style={{ 
                                padding: '2px 8px', 
                                backgroundColor: '#fef3c7', 
                                border: '1px solid #f59e0b',
                                borderRadius: '4px',
                                fontSize: '13px',
                                fontWeight: 600,
                                color: '#92400e'
                              }}>
                                {err.row ?? '-'}
                              </span>
                            </td>
                            <td>
                              <span style={{ 
                                display: 'inline-block',
                                padding: '3px 8px', 
                                backgroundColor: '#fff3cd', 
                                color: '#856404',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 600
                              }}>
                                {err.field}
                              </span>
                            </td>
                            <td style={{ color: '#92400e', fontSize: '13px' }}>
                              {err.message}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Other Errors */}
              {importResult.errors.filter((err: ImportError) => err.type !== 'duplicate').length > 0 && (
                <div>
                  <h4 style={{ marginBottom: '1rem' }}>Error Details</h4>
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th style={{ width: '80px' }}>Row</th>
                          <th style={{ width: '150px' }}>Order Number</th>
                          <th style={{ width: '150px' }}>Field</th>
                          <th>Error Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.errors
                          .filter((err: ImportError) => err.type !== 'duplicate')
                          .map((err: ImportError, index: number) => (
                          <tr key={index}>
                            <td>
                              {err.row !== null && err.row !== undefined ? (
                                <span style={{ 
                                  padding: '2px 8px', 
                                  backgroundColor: '#fee2e2', 
                                  borderRadius: '4px',
                                  fontSize: '0.875rem',
                                  fontWeight: '600'
                                }}>
                                  {err.row}
                                </span>
                              ) : (
                                <span style={{ color: '#9ca3af' }}>-</span>
                              )}
                            </td>
                            <td>
                              {err.order_number || <span style={{ color: '#9ca3af' }}>-</span>}
                            </td>
                            <td>
                              <code style={{ 
                                fontSize: '0.875rem', 
                                backgroundColor: '#f1f5f9', 
                                padding: '2px 6px', 
                                borderRadius: '3px' 
                              }}>
                                {err.field}
                              </code>
                            </td>
                            <td style={{ color: '#7f1d1d' }}>
                              {err.message}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Help Text */}
      {!importResult && !importing && !error && (
        <div style={{
          padding: '1.5rem',
          backgroundColor: '#f0f9ff',
          borderRadius: '8px',
          border: '1px solid #bae6fd'
        }}>
          <h4 style={{ marginTop: 0, marginBottom: '0.75rem', color: '#0c4a6e' }}>
            Import Instructions
          </h4>
          <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#475569', fontSize: '0.875rem', lineHeight: '1.6' }}>
            <li>Select the appropriate format for your import file (vineretail or Magento)</li>
            <li>Choose a CSV or Excel file containing order data</li>
            <li>The system will validate all data and report any errors with row numbers</li>
            <li>Duplicate orders (same order number) will be automatically skipped</li>
            <li>Orders with the same email or phone as an existing order in the same channel will be flagged as duplicates</li>
            <li>Successfully imported orders will be created with "pending" status</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default OrderImport;
